// Sugalarm — voice session safety predicates (cycle 383).
//
// PURE. These two decisions are the safety-critical half of the voice
// session state machine, so they live outside the hook where they can be
// unit-tested. `useVoiceCommands` must not re-implement either inline.
//
// ─── WHY THESE EXIST AS SEPARATE, TESTED FUNCTIONS ─────────────────────
// The first implementation of this cycle expressed both decisions inline,
// against React state, and BOTH were wrong in the same way. `useTTS()`
// returns a fresh object literal on every render, so every callback that
// closed over it churned identity and the "speech finished" effect ran on
// EVERY RENDER rather than on the `tts.speaking` edge. Two consequences,
// both caught in review:
//
//   1. ECHO GUARD DEFEATED. `tts.speaking` only flips true when playback
//      actually STARTS (there is a server fetch first), so right after
//      `speak(confirmBack)` the effect saw `speaking === false`, decided
//      speech was over, and OPENED THE MICROPHONE BEFORE THE CONFIRM-BACK
//      PLAYED — a sentence that literally contains the word "yes".
//   2. ALARM COULD NOT CUT A SPOKEN ANSWER. The same early run set the
//      phase to "idle" while an answer was still being fetched, so the
//      alarm-cancel effect's `phase === "idle"` early-return fired and
//      `tts.stop()` never ran. The answer kept talking over a sounding
//      alarm.
//
// The lesson generalises: NEVER infer "our speech has finished" from a
// single `speaking === false` sample. Speech is finished only after a
// true → false TRANSITION of a latch we set ourselves. Both predicates
// below take that latch explicitly so the caller cannot skip it.

/** Everything the session needs to decide whether to keep the mic shut. */
export interface SpeechGateInput {
  /** An alarm episode is live (`episode.isLive`). READ-ONLY input. */
  alarmLive: boolean;
  /** We asked TTS to speak and that utterance has not finished yet.
   *  Set synchronously at `speak()` time — NOT `tts.speaking`, which lags
   *  behind by a whole network round-trip. */
  ourSpeechInFlight: boolean;
  /** The shared TTS is audibly speaking right now (`tts.speaking`). */
  ttsSpeaking: boolean;
}

/**
 * ECHO GUARD + alarm deferral: may the microphone be opened right now?
 *
 * False while an alarm is live (voice is fully deferred), and false while
 * ANY speech of ours is outstanding — whether it has started playing yet
 * or not. That second clause is the whole echo guard: the device must not
 * be able to hear its own confirm-back.
 */
export function canOpenMic(input: SpeechGateInput): boolean {
  if (input.alarmLive) return false;
  if (input.ourSpeechInFlight) return false;
  if (input.ttsSpeaking) return false;
  return true;
}

export interface AlarmCancelInput {
  /** An alarm episode is live (`episode.isLive`). */
  alarmLive: boolean;
  /** The session is doing something: listening, processing, or speaking. */
  sessionActive: boolean;
  /** A dose is armed and awaiting a yes/no. */
  hasPendingDose: boolean;
  /** We asked TTS to speak and that utterance has not finished yet. */
  ourSpeechInFlight: boolean;
}

/**
 * Should a live alarm HARD-CANCEL the voice session (stop the recognizer
 * AND cut our in-flight speech)?
 *
 * `ourSpeechInFlight` is deliberately its own term rather than being
 * folded into `sessionActive`: it is the exact case the original bug got
 * wrong, where phase bookkeeping said "idle" while an answer was still
 * being spoken.
 *
 * It never cuts speech we did not start — a reminder or an auto-announce
 * is somebody else's utterance and is not ours to stop.
 */
export function shouldCancelForAlarm(input: AlarmCancelInput): boolean {
  if (!input.alarmLive) return false;
  return input.sessionActive || input.hasPendingDose || input.ourSpeechInFlight;
}

/**
 * May we speak at all right now?
 *
 * An alarm outranks every spoken answer, unconditionally.
 *
 * ⚠ THIS EXISTS BECAUSE THE FIX FOR THE FIRST BUG INTRODUCED A SECOND ONE.
 * The alarm-cancel effect latches (one cancel per alarm, so teardown does
 * not re-run on every render). That latch means nothing is left to cut a
 * LATER utterance — and later utterances are reachable: an awaited
 * `POST /api/treatments` resolving after the alarm fired, or a late
 * recognizer error. Relying on "something downstream will cancel it" was
 * the exact shape of the original defect, so the refusal lives at the
 * source, where every call site inherits it.
 *
 * Corollary worth keeping: do NOT re-express this as a check on the
 * session phase. Phase is bookkeeping; `alarmLive` is the fact.
 */
export function canSpeak(input: { alarmLive: boolean }): boolean {
  return !input.alarmLive;
}
